import React, { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import { formatEventTime } from '@/lib/eventTime';
import EventCover from './EventCover';
import useInternalCoverBlob from './useInternalCoverBlob';
import { isToday } from './calendarItems';

function CoverThumb({ item }) {
  const url = useInternalCoverBlob(item.raw?.cover_image_url);
  return (
    <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md">
      <EventCover coverUrl={url} title={item.title} category={item.raw?.category} variant="thumb" />
    </div>
  );
}

function Row({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      data-testid={`list-item-${item.key}`}
      className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-slate-300 hover:shadow-sm"
    >
      <span className={`h-9 w-1 shrink-0 rounded-full ${item.theme.bar}`} />
      {item.kind === 'event' && item.raw?.cover_image_url ? (
        <CoverThumb item={item} />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${item.theme.soft}`}>
            {item.theme.label}
          </span>
          <span className="truncate text-sm font-semibold text-slate-900">{item.title}</span>
        </div>
        {item.subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{item.subtitle}</p>}
      </div>
      <div className="shrink-0 text-right text-xs text-slate-500">
        {item.allDay ? 'All day' : formatEventTime(item.start.toISOString(), item.raw?.timezone)}
      </div>
    </button>
  );
}

export default function ListView({ items, onSelect }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.start.toDateString();
      if (!map.has(key)) map.set(key, { date: it.start, items: [] });
      map.get(key).items.push(it);
    }
    return Array.from(map.values()).sort((a, b) => a.date - b.date);
  }, [items]);

  if (groups.length === 0) return null;

  return (
    <div className="space-y-5" data-testid="calendar-list-view">
      {groups.map((g) => (
        <div key={g.date.toDateString()}>
          <div className="mb-2 flex items-center gap-2">
            <CalendarDays size={14} className="text-slate-400" />
            <h3 className={`text-sm font-semibold ${isToday(g.date) ? 'text-slate-900' : 'text-slate-700'}`}>
              {g.date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              {isToday(g.date) && <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium text-white">Today</span>}
            </h3>
          </div>
          <div className="space-y-2">
            {g.items.map((it) => <Row key={it.key} item={it} onSelect={onSelect} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
